import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FileText,
  Download,
  Users,
  ScrollText,
  Loader2,
  SlidersHorizontal,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  Info,
  ShieldAlert,
  Printer,
  Lock,
  Share2,
  Trash2,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { useLifely } from '../context/LifelyContext';
import { poaHoldersAPI, otherAdvisorsAPI, formalArrangementsAPI, childrenAPI, aiAPI } from '../services/api';
import { toast } from 'sonner';
import { getTypeLabel, getSubtypeLabel } from '../store/mockData';
import UpgradeModal from '../components/billing/UpgradeModal';

// PDF generation
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Field definitions for each map type - defines which fields to show in export
const MAP_FIELD_DEFINITIONS = {
  crypto: {
    fields: [
      { key: 'label', label: 'Name/Label' },
      { key: 'holdingType', label: 'Holding Type' },
      { key: 'cryptoNetwork', label: 'Network/Chain' },
      { key: 'networksUsed', label: 'Networks Used' },
      { key: 'tokenName', label: 'Token/Coin' },
      { key: 'cryptoApproxValue', label: 'Approx. Value' },
      { key: 'exchangePlatform', label: 'Exchange/Platform' },
      { key: 'cryptoAccountEmail', label: 'Account Email' },
      { key: 'cryptoTwoFactor', label: '2FA Method' },
      { key: 'hardwareBrand', label: 'Hardware Wallet' },
      { key: 'deviceLocation', label: 'Device Location' },
      { key: 'passphraseUsed', label: 'Passphrase Used', transform: v => v ? 'Yes' : 'No' },
      { key: 'numAccounts', label: 'No. of Accounts/Wallets' },
      { key: 'walletApp', label: 'Wallet App' },
      { key: 'deviceOn', label: 'Wallet Device' },
      { key: 'numValidators', label: 'No. of Validators' },
      { key: 'nodeHosting', label: 'Node Hosting' },
      { key: 'hostingProvider', label: 'Hosting Location' },
      { key: 'withdrawalAddress', label: 'Withdrawal Address' },
      { key: 'clientSoftware', label: 'Client Software' },
      { key: 'accessNodeInstructions', label: 'How to Access Node' },
      { key: 'monitoringAlerts', label: 'Monitoring/Alerts' },
      { key: 'nomineeInstructions', label: 'Exit Instructions' },
      { key: 'protocolPlatform', label: 'Protocol / Platform' },
      { key: 'amountStaked', label: 'Amount Staked' },
      { key: 'lockupPeriod', label: 'Unbonding Period' },
      { key: 'walletHolding', label: 'Wallet (holding position)' },
      { key: 'howToExit', label: 'How to Exit / Unstake' },
      { key: 'defiPositionType', label: 'Position Type' },
      { key: 'assetsInvolved', label: 'Assets Involved' },
      { key: 'walletAddress', label: 'Wallet Address' },
      { key: 'liquidationRisk', label: 'Liquidation Risk', transform: v => v ? 'Yes - active borrowing' : 'No' },
      { key: 'collectionName', label: 'Collection Name' },
      { key: 'numItems', label: 'Number of NFTs' },
      { key: 'marketplace', label: 'Marketplace' },
      { key: 'signersRequired', label: 'Signers Required' },
      { key: 'otherSigners', label: 'Other Signers' },
      { key: 'bridgeUsed', label: 'Bridge Used' },
      { key: 'withdrawalToL1', label: 'Withdrawal to L1' },
      { key: 'selfCustody', label: 'Self-Custody', transform: v => v ? 'Yes' : 'No' },
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
  shares: {
    fields: [
      { key: 'label', label: 'Investment Name' },
      { key: 'accountType', label: 'Account Type' },
      { key: 'broker', label: 'Broker/Platform' },
      { key: 'tickerSymbol', label: 'Ticker/ISIN' },
      { key: 'sharesHoldings', label: 'Approximate Holdings' },
      { key: 'jointAccount', label: 'Joint Account', transform: v => v ? 'Yes' : 'No' },
      { key: 'accountReference', label: 'Account Ref.' },
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
  property: {
    fields: [
      { key: 'label', label: 'Property Name' },
      { key: 'propertyType', label: 'Property Type' },
      { key: 'propertyAddress', label: 'Address' },
      { key: 'propertyApproxValue', label: 'Approx. Value' },
      { key: 'ownershipStatus', label: 'Ownership', transform: v => v?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) },
      { key: 'jointOwnershipDetails', label: 'Joint Ownership' },
      { key: 'councilAuthority', label: 'Council/Authority' },
      { key: 'deedLocation', label: 'Deed Location' },
      { key: 'mortgageProvider', label: 'Mortgage Provider' },
      { key: 'keyLocation', label: 'Key Location' },
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
  vehicles: {
    fields: [
      { key: 'label', label: 'Vehicle Name' },
      { key: 'type', label: 'Type', transform: (v, e, mapId) => getTypeLabel(mapId, v) },
      { key: 'vehicleYear', label: 'Year' },
      { key: 'vehicleMake', label: 'Make' },
      { key: 'vehicleModel', label: 'Model' },
      { key: 'vehicleRegistration', label: 'Registration' },
      { key: 'vehicleApproxValue', label: 'Approx. Value' },
      { key: 'vehicleOwnershipStatus', label: 'Ownership' },
      { key: 'financeCompany', label: 'Finance Company' },
      { key: 'storageLocation', label: 'Storage Location' },
      { key: 'insuranceDetails', label: 'Insurance Details' },
      { key: 'serviceContact', label: 'Service Contact' },
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
  insurance: {
    fields: [
      { key: 'label', label: 'Policy Name' },
      { key: 'insuranceType', label: 'Insurance Type' },
      { key: 'insuranceProvider', label: 'Provider' },
      { key: 'policyReference', label: 'Policy Ref.' },
      { key: 'coverageAmount', label: 'Coverage Amount' },
      { key: 'renewalDate', label: 'Renewal Date' },
      { key: 'insuranceAutoRenewal', label: 'Auto-renewal', transform: v => v ? 'Yes' : 'No' },
      { key: 'jointNamedParties', label: 'Joint/Named Parties' },
      { key: 'beneficiaries', label: 'Beneficiaries' },
      { key: 'agentContact', label: 'Agent/Broker' },
      { key: 'policyDocLocation', label: 'Documents Location' },
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
  liabilities: {
    fields: [
      { key: 'label', label: 'Debt / Obligation' },
      { key: 'type', label: 'Type', transform: (v, e, mapId) => getTypeLabel(mapId, v) },
      { key: 'lenderName', label: 'Lender / Creditor' },
      { key: 'lenderAccountRef', label: 'Account/Loan Ref.' },
      { key: 'totalOwed', label: 'Approx. Total Owed' },
      { key: 'securedAgainstProperty', label: 'Secured Against Property', transform: v => v ? 'Yes' : 'No' },
      { key: 'securedAgainst', label: 'Secured Against' },
      { key: 'lenderContact', label: 'Lender Contact' },
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
  art: {
    fields: [
      { key: 'label', label: 'Item Name' },
      { key: 'artCategory', label: 'Category' },
      { key: 'artistCreator', label: 'Artist/Creator' },
      { key: 'yearPeriod', label: 'Year/Period' },
      { key: 'artQuantity', label: 'Quantity' },
      { key: 'appraisedValue', label: 'Appraised Value' },
      { key: 'appraisalDate', label: 'Appraisal Date' },
      { key: 'purchasePrice', label: 'Purchase Price' },
      { key: 'artStorageLocation', label: 'Storage Location' },
      { key: 'artInsurance', label: 'Insurance Details' },
      { key: 'intendedDisposition', label: 'Disposition', transform: v => v?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) },
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
  pets: {
    fields: [
      { key: 'label', label: 'Pet Name' },
      { key: 'type', label: 'Type', transform: (v, e, mapId) => getTypeLabel(mapId, v) },
      { key: 'petBreed', label: 'Breed/Species' },
      { key: 'petDateOfBirth', label: 'DOB/Age' },
      { key: 'microchipNumber', label: 'Microchip Number' },
      { key: 'vetContact', label: 'Vet Contact' },
      { key: 'feedingCareRoutine', label: 'Feeding & Care Routine' },
      { key: 'emergencyCaregiver', label: 'Emergency Caregiver' },
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
  physical: {
    fields: [
      { key: 'label', label: 'Document Name' },
      { key: 'documentType', label: 'Document Type' },
      { key: 'physicalLocation', label: 'Physical Location' },
      { key: 'documentFormat', label: 'Format', transform: v => {
        const labels = { 'original': 'Original', 'certified-copy': 'Certified Copy', 'photocopy': 'Photocopy', 'digital-scan': 'Digital Scan', 'both': 'Physical + Digital' };
        return labels[v] || v || '';
      }},
      { key: 'numberOfCopies', label: 'Number of Copies' },
      { key: 'digitalBackupExists', label: 'Digital Backup', transform: v => v ? 'Yes' : 'No' },
      { key: 'relatedTo', label: 'Related To' },
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
  social: {
    fields: [
      { key: 'label', label: 'Account Name' },
      { key: 'platformName', label: 'Platform' },
      { key: 'socialUsername', label: 'Username/Handle' },
      { key: 'profileUrl', label: 'Profile URL' },
      { key: 'socialEmail', label: 'Associated Email' },
      { key: 'twoFactorMethod', label: '2FA Method' },
      { key: 'paidAccount', label: 'Paid Account', transform: v => v ? 'Yes' : 'No' },
      { key: 'socialMonthlyCost', label: 'Monthly Cost' },
      { key: 'socialAutoRenewalDate', label: 'Renewal Date' },
      { key: 'accountDisposition', label: 'Disposition', transform: v => {
        const labels = { 'memorialize': 'Memorialize', 'archive': 'Keep Active', 'close': 'Close/Delete', 'transfer': 'Transfer' };
        return labels[v] || v?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';
      }},
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
  subscriptions: {
    fields: [
      { key: 'label', label: 'Service Name' },
      { key: 'serviceType', label: 'Service Type' },
      { key: 'subProvider', label: 'Provider/Company' },
      { key: 'subAccountEmail', label: 'Account Email' },
      { key: 'subMonthlyCost', label: 'Monthly Cost' },
      { key: 'billingCycle', label: 'Billing Cycle' },
      { key: 'paymentMethod', label: 'Payment Method' },
      { key: 'subAutoRenewal', label: 'Auto-renewal', transform: v => v ? 'Yes' : 'No' },
      { key: 'cancellationInstructions', label: 'Cancellation Instructions' },
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
  business: {
    fields: [
      { key: 'label', label: 'Business Name' },
      { key: 'businessType', label: 'Business Type' },
      { key: 'businessRole', label: 'Your Role' },
      { key: 'ownershipPercentage', label: 'Ownership %' },
      { key: 'companyRegNumber', label: 'Company Reg. No.' },
      { key: 'registeredAddress', label: 'Registered Address' },
      { key: 'accountantContact', label: 'Accountant' },
      { key: 'otherDirectors', label: 'Other Directors/Partners' },
      { key: 'businessBank', label: 'Business Bank' },
      { key: 'businessApproxValue', label: 'Approx. Value' },
      { key: 'keyDocumentsLocation', label: 'Key Documents Location' },
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
  'final-wishes': {
    fields: [
      { key: 'label', label: 'Item' },
      { key: 'type', label: 'Type', transform: (v, e, mapId) => getTypeLabel(mapId, v) },
      { key: 'location', label: 'Details' },
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
  'other-info': {
    fields: [
      { key: 'label', label: 'Item' },
      { key: 'type', label: 'Type', transform: (v, e, mapId) => getTypeLabel(mapId, v) },
      { key: 'location', label: 'Location' },
      { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
      { key: 'firstAction', label: 'First Action' },
      { key: 'notes', label: 'Notes' },
    ]
  },
};

// Default fields for maps without specific definitions
const DEFAULT_FIELDS = [
  { key: 'label', label: 'Name/Label' },
  { key: 'type', label: 'Type' },
  { key: 'location', label: 'Location' },
  { key: 'priority', label: 'Priority', transform: v => v?.charAt(0).toUpperCase() + v?.slice(1) },
  { key: 'firstAction', label: 'First Action' },
  { key: 'notes', label: 'Notes' },
];

// Crypto export is split into one table per holding type, each with a tailored column subset.
// `value` matches the holdingType saved in custom_fields; `columns` are keys from MAP_FIELD_DEFINITIONS.crypto.
const CRYPTO_HOLDING_GROUPS = [
  { value: 'exchange', title: 'Exchange / Custodial Accounts',
    columns: ['label', 'cryptoNetwork', 'networksUsed', 'cryptoApproxValue', 'exchangePlatform', 'cryptoAccountEmail', 'cryptoTwoFactor', 'priority', 'firstAction', 'notes'] },
  { value: 'self-custody-hardware', title: 'Self-Custody Wallets (Hardware)',
    columns: ['label', 'cryptoNetwork', 'networksUsed', 'cryptoApproxValue', 'hardwareBrand', 'deviceLocation', 'passphraseUsed', 'numAccounts', 'selfCustody', 'priority', 'firstAction', 'notes'] },
  { value: 'self-custody-software', title: 'Self-Custody Wallets (Software / Hot)',
    columns: ['label', 'cryptoNetwork', 'networksUsed', 'cryptoApproxValue', 'walletApp', 'deviceOn', 'passphraseUsed', 'selfCustody', 'priority', 'firstAction', 'notes'] },
  { value: 'validator', title: 'Validator Nodes',
    columns: ['label', 'cryptoNetwork', 'numValidators', 'cryptoApproxValue', 'nodeHosting', 'hostingProvider', 'withdrawalAddress', 'clientSoftware', 'accessNodeInstructions', 'monitoringAlerts', 'nomineeInstructions', 'priority', 'firstAction', 'notes'] },
  { value: 'staking', title: 'Staking Positions',
    columns: ['label', 'cryptoNetwork', 'cryptoApproxValue', 'protocolPlatform', 'amountStaked', 'lockupPeriod', 'walletHolding', 'howToExit', 'priority', 'firstAction', 'notes'] },
  { value: 'defi', title: 'DeFi Positions',
    columns: ['label', 'cryptoNetwork', 'cryptoApproxValue', 'protocolPlatform', 'defiPositionType', 'assetsInvolved', 'walletAddress', 'howToExit', 'liquidationRisk', 'priority', 'firstAction', 'notes'] },
  { value: 'nft', title: 'NFT Collections',
    columns: ['label', 'cryptoNetwork', 'cryptoApproxValue', 'collectionName', 'numItems', 'marketplace', 'walletHolding', 'priority', 'firstAction', 'notes'] },
  { value: 'multisig', title: 'Multi-signature Wallets',
    columns: ['label', 'cryptoNetwork', 'cryptoApproxValue', 'protocolPlatform', 'signersRequired', 'otherSigners', 'walletAddress', 'priority', 'firstAction', 'notes'] },
  { value: 'layer2', title: 'Layer 2 / Bridge Positions',
    columns: ['label', 'cryptoNetwork', 'cryptoApproxValue', 'bridgeUsed', 'walletAddress', 'withdrawalToL1', 'priority', 'firstAction', 'notes'] },
  { value: 'other', title: 'Other',
    columns: ['label', 'cryptoNetwork', 'networksUsed', 'cryptoApproxValue', 'selfCustody', 'priority', 'firstAction', 'notes'] },
  { value: 'not-specified', title: 'Unspecified Holding Type',
    columns: ['label', 'cryptoNetwork', 'networksUsed', 'cryptoApproxValue', 'selfCustody', 'priority', 'firstAction', 'notes'] },
];

// Get field value with optional transformation
const getFieldValue = (entry, field, mapId) => {
  const value = entry[field.key];
  if (value === null || value === undefined || value === '' || value === 'not-specified') return '';
  if (field.transform) return field.transform(value, entry, mapId);
  return String(value);
};

// Get fields for a map
const getFieldsForMap = (mapId) => {
  return MAP_FIELD_DEFINITIONS[mapId]?.fields || DEFAULT_FIELDS;
};

// REDACT_KEYS — access / recovery / location hints that must NEVER leave the device
// for the AI. Defence in depth: even though the crypto seed/PIN/key/access-info fields
// were removed from the form, legacy entries may still hold these keys in custom_fields,
// and other maps carry physical access hints (key location, who-has-access). These keys
// are stripped from the PLAIN-TEXT DIGEST only. The structured Details keep everything
// verbatim (they render on-device and never go to the API). Two outputs, two policies.
const REDACT_KEYS = new Set([
  'seedPhraseLocation',   // crypto recovery-phrase location
  'pinLocation',          // crypto device PIN location
  'cryptoKeyLocation',    // private / signing key location
  'accessInfoLocation',   // account access / recovery info location
  'keyLocation',          // physical key location (property / vehicles)
  'accessInfo',           // who-has-access / access details (physical records)
  'pinCode', 'password', 'passcode', 'privateKey', 'recoveryLocation', // defensive extras
]);

// ============================================
// Shared vault assembly (single source of truth)
// ============================================
// Gathers the WHOLE vault — using the EXACT same getFieldsForMap/getFieldValue
// the PDF/CSV use — and returns BOTH a structured form (for the deterministic
// "Details" section) and a compact labelled plain-text digest (for the AI prompt).
// Because both come from the same gathered fields, the narrative and the Details
// section can never describe different things.
export function assembleVaultDigest({
  maps = [],
  entries = {},
  mapNotes = {},
  mapWishes = {},
  willStatus = null,
  poaHolders = [],
  otherAdvisors = [],
  formalArrangements = [],
  children = [],
  contacts = [],
  userName = '',
} = {}) {
  const clean = (v) => (v === null || v === undefined ? '' : String(v).trim());
  const joinDot = (arr) => arr.map(clean).filter(Boolean).join(' · ');

  // ── Maps ──────────────────────────────────────────────────────────
  const mapsOut = maps.map((map) => {
    const fields = getFieldsForMap(map.id);
    const mapEntries = entries[map.id] || [];
    const entriesOut = mapEntries.map((entry) => ({
      label: clean(entry.label) || 'Untitled',
      // `key` is carried so the plain-text digest can redact sensitive fields;
      // the structured Details keep the value verbatim regardless.
      fields: fields
        .map((f) => ({ key: f.key, label: f.label, value: getFieldValue(entry, f, map.id) }))
        .filter((kv) => clean(kv.value) !== ''),
    }));
    const wishes = mapWishes[map.id];
    return {
      id: map.id,
      name: map.name,
      description: map.description || '',
      notes: clean(mapNotes[map.id]),
      wishesContactName: clean(wishes?.contactName),
      wishesNotes: clean(wishes?.notes),
      entries: entriesOut,
    };
  });

  // ── Will & Legal ──────────────────────────────────────────────────
  let will = null;
  if (willStatus && Object.keys(willStatus).length > 0) {
    const ws = willStatus;
    const exists = clean(ws.willExists) || 'unsure';
    const summary = [{ label: 'Will exists', value: exists === 'yes' ? 'Yes' : exists === 'no' ? 'No' : 'Unsure' }];
    if (exists === 'yes') {
      if (clean(ws.dateCreated)) summary.push({ label: 'Date created', value: clean(ws.dateCreated) });
      if (clean(ws.dateUpdated)) summary.push({ label: 'Last updated', value: clean(ws.dateUpdated) });
      if (clean(ws.heldWith) || clean(ws.firmName)) summary.push({ label: 'Held with', value: clean(ws.firmName) || clean(ws.heldWith) });
      if (clean(ws.location)) summary.push({ label: 'Location', value: clean(ws.location) });
      if (clean(ws.lastReviewedDate)) summary.push({ label: 'Last reviewed', value: clean(ws.lastReviewedDate) });
    }

    const executors = [];
    [['Primary executor', ws.executor1], ['Secondary executor', ws.executor2]].forEach(([role, ex]) => {
      if (ex && clean(ex.name)) {
        const rows = [{ label: 'Name', value: clean(ex.name) }];
        if (clean(ex.relationship)) rows.push({ label: 'Relationship', value: clean(ex.relationship) });
        if (clean(ex.email)) rows.push({ label: 'Email', value: clean(ex.email) });
        if (clean(ex.phone)) rows.push({ label: 'Phone', value: clean(ex.phone) });
        executors.push({ role, rows });
      }
    });

    const willHolder = (ws.willHolder && clean(ws.willHolder.name)) ? {
      name: clean(ws.willHolder.name), firm: clean(ws.willHolder.firm),
      email: clean(ws.willHolder.email), phone: clean(ws.willHolder.phone),
    } : null;

    const la = ws.legalAdvisor;
    const legalAdvisor = (la && clean(la.name)) ? {
      name: clean(la.name), firm: clean(la.firm || la.role), email: clean(la.email),
      phone: clean(la.phone), address: clean(la.address),
    } : null;

    const sc = ws.scope || {};
    const scope = [];
    if (sc.personalAssets) scope.push('Personal assets');
    if (sc.property) scope.push('Property');
    if (sc.businessInterests) scope.push('Business interests');
    if (sc.digitalAssets) scope.push('Digital assets');
    if (sc.other && clean(sc.otherText)) scope.push(clean(sc.otherText));

    const poa = {
      exists: clean(ws.poaExists) || 'unsure',
      type: clean(ws.poaType),
      activation: clean(ws.poaActivation),
      holders: poaHolders.map((h) => ({ name: clean(h.name), role: clean(h.role), email: clean(h.email), phone: clean(h.phone) }))
        .filter((h) => h.name),
    };

    const guardianshipTrusts = [];
    if (clean(ws.guardianshipExists) === 'yes') {
      guardianshipTrusts.push({ label: 'Guardianship', value: 'Yes' });
      if (clean(ws.guardianshipNotes)) guardianshipTrusts.push({ label: 'Guardianship notes', value: clean(ws.guardianshipNotes) });
    }
    if (clean(ws.trustsExist) === 'yes') {
      guardianshipTrusts.push({ label: 'Trusts', value: 'Yes' });
      if (clean(ws.trustsNotes)) guardianshipTrusts.push({ label: 'Trusts notes', value: clean(ws.trustsNotes) });
    }

    will = {
      summary,
      scope,
      willHolder,
      executors,
      legalAdvisor,
      poa,
      guardianshipTrusts,
      advisors: otherAdvisors.map((a) => ({ name: clean(a.name), type: clean(a.type), email: clean(a.email), phone: clean(a.phone) })).filter((a) => a.name),
      formalArrangements: formalArrangements.map((a) => ({ type: clean(a.type), heldWith: clean(a.held_with), notes: clean(a.notes) })).filter((a) => a.type),
      children: children.map((c) => ({ name: clean(c.name), dob: clean(c.date_of_birth), guardian: clean(c.intended_guardian_name), notes: clean(c.notes) })).filter((c) => c.name),
      notes: clean(ws.notes),
    };
  }

  const contactsOut = (contacts || []).map((c) => ({
    name: clean(c.name), category: clean(c.category), role: clean(c.role || c.relationship),
    email: clean(c.email), phone: clean(c.phone), notes: clean(c.notes),
  })).filter((c) => c.name);

  const details = { userName: clean(userName), maps: mapsOut, will, contacts: contactsOut };

  // ── Plain-text digest (compact, labelled — what the model receives) ──
  // REDACTED: REDACT_KEYS are stripped here. The structured `details` above is
  // NOT redacted (on-device render only). Two outputs, two policies.
  const L = [];
  L.push(`ESTATE INFORMATION DIGEST${details.userName ? ` - ${details.userName}` : ''}`);

  L.push('');
  L.push('== ASSETS & RECORDS ==');
  const mapsWithContent = mapsOut.filter((m) => m.entries.length || m.notes || m.wishesContactName || m.wishesNotes);
  if (mapsWithContent.length === 0) L.push('(nothing recorded yet)');
  mapsWithContent.forEach((m) => {
    L.push('');
    L.push(`# ${m.name}${m.entries.length ? ` (${m.entries.length})` : ''}`);
    if (m.notes) L.push(`Notes & intent: ${m.notes}`);
    if (m.wishesContactName || m.wishesNotes) {
      L.push(`Wishes: ${[m.wishesContactName && `intended for ${m.wishesContactName}`, m.wishesNotes].filter(Boolean).join(' - ')}`);
    }
    m.entries.forEach((e) => {
      L.push(`- ${e.label}`);
      e.fields.forEach((kv) => {
        if (REDACT_KEYS.has(kv.key)) return; // never send access/recovery hints to the model
        L.push(`    ${kv.label}: ${kv.value}`);
      });
    });
  });

  if (will) {
    L.push('');
    L.push('== WILL & LEGAL ==');
    will.summary.forEach((kv) => L.push(`${kv.label}: ${kv.value}`));
    if (will.scope.length) L.push(`Scope: ${will.scope.join(', ')}`);
    if (will.willHolder) L.push(`Will holder: ${joinDot([will.willHolder.name, will.willHolder.firm, will.willHolder.email, will.willHolder.phone])}`);
    will.executors.forEach((ex) => L.push(`${ex.role}: ${joinDot(ex.rows.map((r) => r.value))}`));
    if (will.legalAdvisor) L.push(`Legal advisor: ${joinDot([will.legalAdvisor.name, will.legalAdvisor.firm, will.legalAdvisor.email, will.legalAdvisor.phone, will.legalAdvisor.address])}`);
    if (will.poa.exists === 'yes' || will.poa.holders.length) {
      L.push(`Power of attorney: ${will.poa.exists === 'yes' ? 'Yes' : will.poa.exists}${will.poa.type ? ` (${will.poa.type})` : ''}${will.poa.activation ? ` - activates: ${will.poa.activation}` : ''}`);
      will.poa.holders.forEach((h) => L.push(`  - POA holder: ${joinDot([h.name, h.role, h.email, h.phone])}`));
    }
    will.guardianshipTrusts.forEach((kv) => L.push(`${kv.label}: ${kv.value}`));
    if (will.advisors.length) {
      L.push('Other advisors:');
      will.advisors.forEach((a) => L.push(`  - ${joinDot([a.name, a.type, a.email, a.phone])}`));
    }
    if (will.formalArrangements.length) {
      L.push('Formal arrangements:');
      will.formalArrangements.forEach((a) => L.push(`  - ${joinDot([a.type, a.heldWith && `held with ${a.heldWith}`, a.notes])}`));
    }
    if (will.children.length) {
      L.push('Children & dependents:');
      will.children.forEach((c) => L.push(`  - ${joinDot([c.name, c.dob, c.guardian && `guardian: ${c.guardian}`, c.notes])}`));
    }
    if (will.notes) L.push(`Additional notes: ${will.notes}`);
  }

  if (contactsOut.length) {
    L.push('');
    L.push('== KEY CONTACTS ==');
    contactsOut.forEach((c) => {
      const head = joinDot([c.name, c.category, c.role, c.email, c.phone]);
      L.push(`- ${head}${c.notes ? ` - ${c.notes}` : ''}`);
    });
  }

  return { details, text: L.join('\n') };
}

// CSV generation helper
const generateCSV = (data, filename) => {
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Escape CSV field
const escapeCSV = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// ============================================
// PDF Generation with jsPDF + autoTable
// ============================================
const generatePDF = ({ maps, entries, willStatus, contacts, mapNotes, mapWishes, userName }) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let yPos = 20;

  const today = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
  });

  // Helper to add new page if needed
  const checkPageBreak = (height = 30) => {
    if (yPos + height > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      yPos = 20;
      return true;
    }
    return false;
  };

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Lifely - Estate Information Summary', margin, yPos);
  yPos += 8;
  
  // User name
  if (userName) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(userName, margin, yPos);
    yPos += 6;
  }
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${today}`, margin, yPos);
  yPos += 10;

  // Disclaimer box
  doc.setFillColor(245, 245, 245);
  doc.rect(margin, yPos, pageWidth - margin * 2, 15, 'F');
  doc.setFontSize(8);
  doc.text('Important: This document is for informational purposes only and does not constitute legal advice.', margin + 3, yPos + 5);
  doc.text('It is not a substitute for formal legal documents. All information should be verified.', margin + 3, yPos + 10);
  yPos += 22;

  // Section counter
  let sectionNum = 1;

  // Will Status Section - COMPREHENSIVE
  if (willStatus) {
    checkPageBreak(50);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${sectionNum}. Formal Arrangements`, margin, yPos);
    yPos += 8;
    sectionNum++;

    // Will Details
    const willData = [
      ['Will Exists', willStatus.willExists === 'yes' ? 'Yes' : willStatus.willExists === 'no' ? 'No' : 'Unsure'],
    ];
    if (willStatus.willExists === 'yes') {
      if (willStatus.dateCreated) willData.push(['Date Created', willStatus.dateCreated]);
      if (willStatus.dateUpdated) willData.push(['Last Updated', willStatus.dateUpdated]);
      if (willStatus.heldWith) willData.push(['Held With', willStatus.heldWith]);
      if (willStatus.firmName) willData.push(['Firm/Trustee Name', willStatus.firmName]);
      if (willStatus.location) willData.push(['Physical Location', willStatus.location]);
      if (willStatus.lastReviewedDate) willData.push(['Last Reviewed', willStatus.lastReviewedDate]);
    }
    
    // Will Holder
    if (willStatus.willHolder?.name) {
      willData.push(['Will Holder Contact', `${willStatus.willHolder.name}${willStatus.willHolder.email ? ' - ' + willStatus.willHolder.email : ''}${willStatus.willHolder.phone ? ' - ' + willStatus.willHolder.phone : ''}`]);
    }
    
    // Executors
    if (willStatus.executor1?.name) {
      willData.push(['Primary Executor', `${willStatus.executor1.name}${willStatus.executor1.relationship ? ' (' + willStatus.executor1.relationship + ')' : ''}${willStatus.executor1.email ? ' - ' + willStatus.executor1.email : ''}${willStatus.executor1.phone ? ' - ' + willStatus.executor1.phone : ''}`]);
    }
    if (willStatus.executor2?.name) {
      willData.push(['Secondary Executor', `${willStatus.executor2.name}${willStatus.executor2.relationship ? ' (' + willStatus.executor2.relationship + ')' : ''}${willStatus.executor2.email ? ' - ' + willStatus.executor2.email : ''}`]);
    }
    if (willStatus.executorNotes) {
      willData.push(['Executor Notes', willStatus.executorNotes]);
    }
    
    // Scope
    const scopeItems = [];
    if (willStatus.scope?.personalAssets) scopeItems.push('Personal Assets');
    if (willStatus.scope?.property) scopeItems.push('Property');
    if (willStatus.scope?.businessInterests) scopeItems.push('Business Interests');
    if (willStatus.scope?.digitalAssets) scopeItems.push('Digital Assets');
    if (willStatus.scope?.other && willStatus.scope?.otherText) scopeItems.push(willStatus.scope.otherText);
    if (scopeItems.length > 0) {
      willData.push(['Will Scope', scopeItems.join(', ')]);
    }

    autoTable(doc, {
      startY: yPos,
      head: [['Will & Testament', '']],
      body: willData,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
      headStyles: { fillColor: [80, 80, 80] },
      columnStyles: { 0: { cellWidth: 50, fontStyle: 'bold' }, 1: { cellWidth: 'auto' } },
    });
    yPos = doc.lastAutoTable.finalY + 8;

    // Power of Attorney Section
    if (willStatus.poaExists === 'yes') {
      checkPageBreak(40);
      const poaData = [
        ['POA Exists', 'Yes'],
      ];
      if (willStatus.poaType) poaData.push(['Type', willStatus.poaType]);
      if (willStatus.poaOtherType) poaData.push(['Other Type Details', willStatus.poaOtherType]);
      if (willStatus.poaActivation) poaData.push(['Activation', willStatus.poaActivation]);
      if (willStatus.poaLawFirm) poaData.push(['Law Firm', willStatus.poaLawFirm]);
      if (willStatus.poaAttorneyName) poaData.push(['Appointed Attorney', willStatus.poaAttorneyName]);
      if (willStatus.poaSuccessorName) poaData.push(['Successor Attorney', willStatus.poaSuccessorName]);
      if (willStatus.poaHolders && willStatus.poaHolders.length > 0) {
        willStatus.poaHolders.forEach((holder, idx) => {
          poaData.push([`POA Holder ${idx + 1}`, `${holder.name}${holder.relationship ? ' (' + holder.relationship + ')' : ''}${holder.email ? ' - ' + holder.email : ''}`]);
        });
      }

      autoTable(doc, {
        startY: yPos,
        head: [['Power of Attorney', '']],
        body: poaData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [80, 80, 80] },
        columnStyles: { 0: { cellWidth: 50, fontStyle: 'bold' }, 1: { cellWidth: 'auto' } },
      });
      yPos = doc.lastAutoTable.finalY + 8;
    }

    // Children & Dependents
    if (willStatus.children && willStatus.children.length > 0) {
      checkPageBreak(40);
      const childrenData = willStatus.children.map(child => [
        child.name || '',
        child.dob || '',
        child.intendedGuardianName || '-',
        child.godparent1Name || '-',
        child.arrangementStatus || '-',
        child.specialNeeds || '-',
        child.notes || '-'
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Name', 'DOB', 'Intended Guardian', 'Godparent', 'Arrangement', 'Special Needs', 'Notes']],
        body: childrenData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
        headStyles: { fillColor: [80, 80, 80] },
      });
      yPos = doc.lastAutoTable.finalY + 8;
    }

    // Guardianship & Trusts
    if (willStatus.guardianshipExists === 'yes' || willStatus.trustsExist === 'yes') {
      checkPageBreak(30);
      const otherData = [];
      if (willStatus.guardianshipExists === 'yes') {
        otherData.push(['Guardianship Exists', 'Yes']);
        if (willStatus.guardianshipNotes) otherData.push(['Guardianship Notes', willStatus.guardianshipNotes]);
      }
      if (willStatus.trustsExist === 'yes') {
        otherData.push(['Trusts Exist', 'Yes']);
      }
      
      if (otherData.length > 0) {
        autoTable(doc, {
          startY: yPos,
          head: [['Guardianship & Trusts', '']],
          body: otherData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
          headStyles: { fillColor: [80, 80, 80] },
          columnStyles: { 0: { cellWidth: 50, fontStyle: 'bold' }, 1: { cellWidth: 'auto' } },
        });
        yPos = doc.lastAutoTable.finalY + 8;
      }
    }

    // Legal Advisor
    if (willStatus.legalAdvisor?.name) {
      checkPageBreak(30);
      const advisorData = [
        ['Name', willStatus.legalAdvisor.name],
      ];
      if (willStatus.legalAdvisor.role) advisorData.push(['Role', willStatus.legalAdvisor.role]);
      if (willStatus.legalAdvisor.email) advisorData.push(['Email', willStatus.legalAdvisor.email]);
      if (willStatus.legalAdvisor.phone) advisorData.push(['Phone', willStatus.legalAdvisor.phone]);
      if (willStatus.legalAdvisor.notes) advisorData.push(['Notes', willStatus.legalAdvisor.notes]);

      autoTable(doc, {
        startY: yPos,
        head: [['Legal Advisor', '']],
        body: advisorData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [80, 80, 80] },
        columnStyles: { 0: { cellWidth: 50, fontStyle: 'bold' }, 1: { cellWidth: 'auto' } },
      });
      yPos = doc.lastAutoTable.finalY + 8;
    }

    // Other Advisors
    if (willStatus.otherAdvisors && willStatus.otherAdvisors.length > 0) {
      checkPageBreak(40);
      const advisorsData = willStatus.otherAdvisors.map(adv => [
        adv.type || '',
        adv.name || '',
        adv.email || '-',
        adv.phone || '-',
        adv.notes || '-'
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Type', 'Name', 'Email', 'Phone', 'Notes']],
        body: advisorsData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
        headStyles: { fillColor: [80, 80, 80] },
      });
      yPos = doc.lastAutoTable.finalY + 8;
    }

    // Other Documents
    if (willStatus.otherDocuments && willStatus.otherDocuments.length > 0) {
      checkPageBreak(40);
      const docsData = willStatus.otherDocuments.map(doc => [
        doc.type || '',
        doc.heldWith || '-',
        doc.notes || '-'
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Document Type', 'Held With', 'Notes']],
        body: docsData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
        headStyles: { fillColor: [80, 80, 80] },
      });
      yPos = doc.lastAutoTable.finalY + 8;
    }

    // Lifely Reference & Notes
    if (willStatus.lifelyReferenced === 'yes' || willStatus.notes) {
      checkPageBreak(30);
      const notesData = [];
      if (willStatus.lifelyReferenced === 'yes') {
        notesData.push(['Lifely Referenced in Will', 'Yes']);
      }
      if (willStatus.notes) {
        notesData.push(['Additional Notes', willStatus.notes]);
      }
      
      if (notesData.length > 0) {
        autoTable(doc, {
          startY: yPos,
          head: [['Other Information', '']],
          body: notesData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
          headStyles: { fillColor: [80, 80, 80] },
          columnStyles: { 0: { cellWidth: 50, fontStyle: 'bold' }, 1: { cellWidth: 'auto' } },
        });
        yPos = doc.lastAutoTable.finalY + 10;
      }
    }
  }

  // Contacts Section
  if (contacts && contacts.length > 0) {
    checkPageBreak(50);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${sectionNum}. Key Contacts & Advisors`, margin, yPos);
    yPos += 8;
    sectionNum++;

    const contactData = contacts.map(c => [
      c.name || '',
      c.category || '',
      c.role || c.relationship || '',
      c.email || '',
      c.phone || '',
      c.notes || ''
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Name', 'Category', 'Role', 'Email', 'Phone', 'Notes']],
      body: contactData,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
      headStyles: { fillColor: [80, 80, 80] },
      columnStyles: { 
        0: { cellWidth: 35 }, 
        1: { cellWidth: 25 }, 
        2: { cellWidth: 30 },
        3: { cellWidth: 45 },
        4: { cellWidth: 30 },
        5: { cellWidth: 'auto' }
      },
    });
    yPos = doc.lastAutoTable.finalY + 10;
  }

  // Asset Maps
  maps.forEach((map) => {
    const mapEntries = entries[map.id] || [];
    const notes = mapNotes?.[map.id];
    const wishes = mapWishes?.[map.id];
    const fields = getFieldsForMap(map.id);

    // Always start maps on new page for cleaner layout
    doc.addPage();
    yPos = 20;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${sectionNum}. ${map.name}`, margin, yPos);
    yPos += 6;
    sectionNum++;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100);
    doc.text(map.description || '', margin, yPos);
    doc.setTextColor(0);
    yPos += 8;

    // Notes & Intent
    if (notes) {
      doc.setFillColor(250, 250, 250);
      const notesLines = doc.splitTextToSize(`Notes & Intent: ${notes}`, pageWidth - margin * 2 - 6);
      const notesHeight = notesLines.length * 4 + 6;
      doc.rect(margin, yPos, pageWidth - margin * 2, notesHeight, 'F');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(notesLines, margin + 3, yPos + 4);
      yPos += notesHeight + 4;
    }

    // Wishes
    if (wishes?.contactName || wishes?.notes) {
      doc.setFillColor(245, 250, 255);
      let wishesText = 'Wishes: ';
      if (wishes.contactName) wishesText += `Intended for ${wishes.contactName}. `;
      if (wishes.notes) wishesText += wishes.notes;
      const wishesLines = doc.splitTextToSize(wishesText, pageWidth - margin * 2 - 6);
      const wishesHeight = wishesLines.length * 4 + 6;
      doc.rect(margin, yPos, pageWidth - margin * 2, wishesHeight, 'F');
      doc.setFontSize(8);
      doc.text(wishesLines, margin + 3, yPos + 4);
      yPos += wishesHeight + 4;
    }

    // Shared autoTable column-width rules
    const colWidths = (flds) => flds.reduce((acc, f, i) => {
      if (f.key === 'notes' || f.key === 'firstAction') {
        acc[i] = { cellWidth: 40 };
      } else if (f.key === 'label') {
        acc[i] = { cellWidth: 35 };
      }
      return acc;
    }, {});

    // Entries table(s)
    if (mapEntries.length > 0 && map.id === 'crypto') {
      // Crypto: one tailored table per holding type
      const cryptoFieldsByKey = (MAP_FIELD_DEFINITIONS.crypto?.fields || []).reduce((acc, f) => {
        acc[f.key] = f;
        return acc;
      }, {});

      const groupsByType = mapEntries.reduce((acc, entry) => {
        const t = entry.holdingType || 'not-specified';
        (acc[t] = acc[t] || []).push(entry);
        return acc;
      }, {});

      CRYPTO_HOLDING_GROUPS.forEach((group) => {
        const groupEntries = groupsByType[group.value] || [];
        if (groupEntries.length === 0) return;

        const groupFields = group.columns
          .map((k) => cryptoFieldsByKey[k])
          .filter(Boolean);

        // Group subheading
        checkPageBreak(24);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60);
        doc.text(`${group.title} (${groupEntries.length})`, margin, yPos);
        doc.setTextColor(0);
        yPos += 5;

        autoTable(doc, {
          startY: yPos,
          head: [groupFields.map(f => f.label)],
          body: groupEntries.map(entry =>
            groupFields.map(field => getFieldValue(entry, field, map.id) || '-')
          ),
          margin: { left: margin, right: margin },
          styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak', minCellHeight: 8 },
          headStyles: { fillColor: [80, 80, 80], fontSize: 7 },
          columnStyles: colWidths(groupFields),
        });
        yPos = doc.lastAutoTable.finalY + 6;
      });
    } else if (mapEntries.length > 0) {
      const headers = fields.map(f => f.label);
      const tableData = mapEntries.map(entry =>
        fields.map(field => getFieldValue(entry, field, map.id) || '-')
      );

      autoTable(doc, {
        startY: yPos,
        head: [headers],
        body: tableData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak', minCellHeight: 8 },
        headStyles: { fillColor: [80, 80, 80], fontSize: 7 },
        columnStyles: colWidths(fields),
      });
      yPos = doc.lastAutoTable.finalY + 5;
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(120);
      doc.text('No entries recorded for this category.', margin, yPos);
      doc.setTextColor(0);
      yPos += 10;
    }

    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(`Total entries: ${mapEntries.length}`, margin, yPos);
    doc.setTextColor(0);
  });

  // ── Legal Disclaimer Page ──────────────────────────────────────────────
  doc.addPage();
  const dp = { margin: 20, pageW: doc.internal.pageSize.getWidth(), pageH: doc.internal.pageSize.getHeight() };
  let dy = 20;

  // Red header bar
  doc.setFillColor(180, 30, 30);
  doc.rect(0, 0, dp.pageW, 14, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('Legal Disclaimer & Handling Notice', dp.margin, 9);
  doc.setTextColor(0, 0, 0);
  dy = 28;

  // Main warning box
  doc.setFillColor(255, 235, 235);
  doc.setDrawColor(180, 30, 30);
  doc.roundedRect(dp.margin, dy, dp.pageW - dp.margin * 2, 30, 3, 3, 'FD');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(120, 0, 0);
  doc.text('SENSITIVE PERSONAL & FINANCIAL DATA', dp.margin + 6, dy + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 0, 0);
  const mainWarning = doc.splitTextToSize(
    'This document contains highly sensitive personal, financial, and legal information. It should be treated with the same level of care as a passport, will, or financial statement.',
    dp.pageW - dp.margin * 2 - 12
  );
  doc.text(mainWarning, dp.margin + 6, dy + 18);
  doc.setTextColor(0, 0, 0);
  dy += 38;

  // Precautions section
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Precautions You Must Take', dp.margin, dy);
  dy += 8;

  const precautions = [
    ['Printing', 'Only print in a private, secure environment. Collect all pages immediately. Do not leave printed copies unattended or in shared printers.'],
    ['Digital storage', 'If saving digitally, store in an encrypted location (e.g., password-protected folder, encrypted drive). Do not store in unprotected cloud storage or email drafts.'],
    ['Sharing', 'Only share with authorised individuals who have a legitimate need, such as your lawyer, executor, or immediate family. Do not send via standard email without encryption.'],
    ['Disposal', 'When disposing of printed copies, shred them. Permanently delete digital copies that are no longer required.'],
    ['Third parties', 'Before sharing with any professional (lawyer, accountant, financial adviser), verify their identity and confirm they are authorised to receive this information.'],
  ];

  doc.setFontSize(9);
  precautions.forEach(([title, body]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${title}:`, dp.margin + 3, dy);
    const bodyLines = doc.splitTextToSize(body, dp.pageW - dp.margin * 2 - 20);
    doc.setFont('helvetica', 'normal');
    doc.text(bodyLines, dp.margin + 28, dy);
    dy += bodyLines.length * 4.5 + 4;
  });

  dy += 4;

  // Liability disclaimer box
  doc.setFillColor(248, 248, 248);
  doc.setDrawColor(180, 180, 180);
  const disclaimerText = doc.splitTextToSize(
    'LIMITATION OF LIABILITY: Lifely (lifely.guide) provides this export feature as a convenience tool for personal information management only. Lifely cannot be held responsible or liable for any loss, damage, harm, or consequence - financial, legal, reputational, or otherwise - arising from the printing, saving, sharing, misuse, theft, loss, or unauthorised access of this exported document, whether in digital or physical form. The user assumes full responsibility for the secure handling, storage, and distribution of this document from the point of download. This export does not constitute legal, financial, or professional advice. Users should seek independent legal or financial counsel for matters relating to estate planning, asset management, or legal documentation.',
    dp.pageW - dp.margin * 2 - 12
  );
  const disclaimerBoxH = disclaimerText.length * 4.5 + 14;
  doc.roundedRect(dp.margin, dy, dp.pageW - dp.margin * 2, disclaimerBoxH, 3, 3, 'FD');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(60, 60, 60);
  doc.text('LIMITATION OF LIABILITY', dp.margin + 6, dy + 8);
  doc.setFont('helvetica', 'normal');
  const bodyOnly = doc.splitTextToSize(
    'Lifely (lifely.guide) provides this export feature as a convenience tool for personal information management only. Lifely cannot be held responsible or liable for any loss, damage, harm, or consequence - financial, legal, reputational, or otherwise - arising from the printing, saving, sharing, misuse, theft, loss, or unauthorised access of this exported document, whether in digital or physical form. The user assumes full responsibility for the secure handling, storage, and distribution of this document from the point of download. This export does not constitute legal, financial, or professional advice. Users should seek independent legal or financial counsel for matters relating to estate planning, asset management, or legal documentation.',
    dp.pageW - dp.margin * 2 - 12
  );
  doc.text(bodyOnly, dp.margin + 6, dy + 14);
  doc.setTextColor(0, 0, 0);
  dy += disclaimerBoxH + 10;

  // Footer
  doc.setFontSize(7.5);
  doc.setTextColor(120);
  doc.text(`Generated by Lifely (lifely.guide) on ${new Date().toLocaleDateString()}, for authorised use only`, dp.margin, dp.pageH - 10);

  // Save
  const dateStr = new Date().toISOString().split('T')[0];
  doc.save(`lifely-estate-summary-${dateStr}.pdf`);
  toast.success('PDF downloaded successfully');
};

// ============================================
// Summary PDF — AI narrative on top, deterministic Details below.
// The narrative is the only AI-touched part; the Details render verbatim from
// the structured `details` (never the model).
// ============================================
const generateSummaryPDF = ({ summary, details, userName }) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  let yPos = 20;
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const checkBreak = (h = 24) => { if (yPos + h > pageHeight - 20) { doc.addPage(); yPos = 20; } };

  // ── Header ──
  doc.setFontSize(20); doc.setFont('helvetica', 'bold');
  doc.text('Estate Summary', margin, yPos); yPos += 8;
  if (userName) { doc.setFontSize(12); doc.setFont('helvetica', 'normal'); doc.text(userName, margin, yPos); yPos += 6; }
  doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.text(`Generated: ${today}`, margin, yPos); yPos += 10;

  // ── AI narrative ──
  doc.setFontSize(13); doc.setFont('helvetica', 'bold');
  doc.text('A note for your loved ones', margin, yPos); yPos += 7;
  doc.setFontSize(10.5); doc.setFont('helvetica', 'normal');
  (summary || '').split(/\n{2,}/).forEach((para) => {
    const lines = doc.splitTextToSize(para.replace(/\n/g, ' ').trim(), pageWidth - margin * 2);
    lines.forEach((line) => { checkBreak(7); doc.text(line, margin, yPos); yPos += 5.4; });
    yPos += 3;
  });
  yPos += 3;
  checkBreak(10);
  doc.setFontSize(8); doc.setTextColor(130);
  doc.text('This summary was generated with AI from the details you entered. The complete record below is exactly what you entered.', margin, yPos, { maxWidth: pageWidth - margin * 2 });
  doc.setTextColor(0);

  // ── Deterministic "Complete Record" ──
  doc.addPage(); yPos = 20;
  doc.setFontSize(16); doc.setFont('helvetica', 'bold');
  doc.text('Complete Record', margin, yPos); yPos += 5;
  doc.setFontSize(9); doc.setFont('helvetica', 'italic'); doc.setTextColor(110);
  doc.text('Exactly what you entered, not AI-generated.', margin, yPos); doc.setTextColor(0); yPos += 8;

  const kvTable = (title, rows) => {
    const body = (rows || []).filter((r) => r[1] !== undefined && r[1] !== null && String(r[1]).trim() !== '');
    if (body.length === 0) return;
    checkBreak(18);
    autoTable(doc, {
      startY: yPos, head: [[title, '']], body,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 2.5, overflow: 'linebreak' },
      headStyles: { fillColor: [80, 80, 80] },
      columnStyles: { 0: { cellWidth: 52, fontStyle: 'bold' }, 1: { cellWidth: 'auto' } },
    });
    yPos = doc.lastAutoTable.finalY + 6;
  };
  const listTable = (head, rows) => {
    if (!rows || rows.length === 0) return;
    checkBreak(18);
    autoTable(doc, {
      startY: yPos, head: [head], body: rows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
      headStyles: { fillColor: [80, 80, 80] },
    });
    yPos = doc.lastAutoTable.finalY + 6;
  };

  // Maps
  (details.maps || []).forEach((m) => {
    if (!(m.entries.length || m.notes || m.wishesContactName || m.wishesNotes)) return;
    checkBreak(16);
    doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.text(m.name, margin, yPos); yPos += 6;
    if (m.notes) kvTable('Notes & Intent', [['Notes', m.notes]]);
    if (m.wishesContactName || m.wishesNotes) kvTable('Wishes', [['Intended for', m.wishesContactName], ['Notes', m.wishesNotes]]);
    m.entries.forEach((e) => kvTable(e.label, e.fields.map((f) => [f.label, f.value])));
  });

  // Will & Legal
  const w = details.will;
  if (w) {
    checkBreak(16);
    doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.text('Will & Legal', margin, yPos); yPos += 6;
    kvTable('Will', w.summary.map((kv) => [kv.label, kv.value]));
    if (w.scope.length) kvTable('Scope', [['Covers', w.scope.join(', ')]]);
    if (w.willHolder) kvTable('Will Holder', [['Name', w.willHolder.name], ['Firm', w.willHolder.firm], ['Email', w.willHolder.email], ['Phone', w.willHolder.phone]]);
    w.executors.forEach((ex) => kvTable(ex.role, ex.rows.map((r) => [r.label, r.value])));
    if (w.legalAdvisor) kvTable('Legal Advisor', [['Name', w.legalAdvisor.name], ['Firm', w.legalAdvisor.firm], ['Email', w.legalAdvisor.email], ['Phone', w.legalAdvisor.phone], ['Address', w.legalAdvisor.address]]);
    if (w.poa.exists === 'yes' || w.poa.holders.length) {
      kvTable('Power of Attorney', [['Exists', w.poa.exists === 'yes' ? 'Yes' : w.poa.exists], ['Type', w.poa.type], ['Activation', w.poa.activation]]);
      listTable(['POA Holder', 'Role', 'Email', 'Phone'], w.poa.holders.map((h) => [h.name, h.role, h.email, h.phone]));
    }
    if (w.guardianshipTrusts.length) kvTable('Guardianship & Trusts', w.guardianshipTrusts.map((kv) => [kv.label, kv.value]));
    listTable(['Advisor', 'Type', 'Email', 'Phone'], w.advisors.map((a) => [a.name, a.type, a.email, a.phone]));
    listTable(['Arrangement', 'Held With', 'Notes'], w.formalArrangements.map((a) => [a.type, a.heldWith, a.notes]));
    listTable(['Child', 'Date of Birth', 'Intended Guardian', 'Notes'], w.children.map((c) => [c.name, c.dob, c.guardian, c.notes]));
    if (w.notes) kvTable('Additional Notes', [['Notes', w.notes]]);
  }

  // Contacts
  if ((details.contacts || []).length) {
    checkBreak(16);
    doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.text('Key Contacts', margin, yPos); yPos += 6;
    listTable(['Name', 'Category', 'Role', 'Email', 'Phone', 'Notes'], details.contacts.map((c) => [c.name, c.category, c.role, c.email, c.phone, c.notes]));
  }

  // ── Disclaimer footer ──
  doc.addPage();
  doc.setFontSize(11); doc.setFont('helvetica', 'bold');
  doc.text('Handling & Disclaimer', margin, 22);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(80);
  const disc = doc.splitTextToSize('This document contains sensitive personal information and does not constitute legal or financial advice. It is not a substitute for formal legal documents. The AI-written note is a convenience summary and may simplify or omit detail - the Complete Record is the authoritative version. Lifely cannot be held responsible for any loss, damage, or consequence arising from the printing, saving, sharing, misuse, or unauthorised access of this document. Handle and store it securely, and share it only with people you trust.', pageWidth - margin * 2);
  doc.text(disc, margin, 32);
  doc.setTextColor(0);

  doc.save(`lifely-estate-summary-${new Date().toISOString().split('T')[0]}.pdf`);
  toast.success('Summary PDF downloaded');
};

// ============================================
// Main Component
// ============================================
const PrintExport = () => {
  const { 
    maps, 
    user,
    fetchEntriesForMap,
    getMapNotes,
    getMapWishes,
    willStatus: contextWillStatus, 
    contacts: contextContacts, 
    fetchMaps,
    fetchContacts,
    fetchWillStatus,
    isAuthenticated,
    loading
  } = useLifely();

  // Export is a paid feature — only standard/plus may export; trial/free/expired cannot.
  const canExport = ['standard', 'plus'].includes(user?.plan);
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Run an export action only if the plan allows it, otherwise prompt to upgrade.
  const requireExport = (fn) => {
    if (!canExport) {
      setShowUpgrade(true);
      return;
    }
    withDisclaimer(fn);
  };

  const [dataLoaded, setDataLoaded] = useState(false);
  const [entriesLoaded, setEntriesLoaded] = useState(false);
  const [allEntries, setAllEntries] = useState({});
  const [allMapNotes, setAllMapNotes] = useState({});
  const [allMapWishes, setAllMapWishes] = useState({});
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [summaryGenerating, setSummaryGenerating] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  // { summary, details, text } — kept so Regenerate can re-POST the same digest
  // and Download can build the PDF from the SAME details (never re-derived).
  const [summaryPreview, setSummaryPreview] = useState(null);

  // Disclaimer modal
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerAck, setDisclaimerAck] = useState(false);
  const pendingExportRef = useRef(null);

  const withDisclaimer = useCallback((fn) => {
    pendingExportRef.current = fn;
    setDisclaimerAck(false);
    setShowDisclaimer(true);
  }, []);

  const handleDisclaimerConfirm = useCallback(() => {
    setShowDisclaimer(false);
    if (pendingExportRef.current) {
      pendingExportRef.current();
      pendingExportRef.current = null;
    }
  }, []);

  // Will & Legal sub-tables (live in their own tables; the context willStatus
  // object hard-codes these to [] — see Part A bug fix).
  const [poaHolders, setPoaHolders] = useState([]);
  const [otherAdvisors, setOtherAdvisors] = useState([]);
  const [formalArrangements, setFormalArrangements] = useState([]);
  const [childrenRows, setChildrenRows] = useState([]);

  // Selection state
  const [selectedMaps, setSelectedMaps] = useState({});
  const [includeWillStatus, setIncludeWillStatus] = useState(true);
  const [includeContacts, setIncludeContacts] = useState(true);
  
  // Contact-specific export
  const [selectedContactId, setSelectedContactId] = useState('');
  
  // View state
  const [mapsExpanded, setMapsExpanded] = useState(true);

  // Filter out formal-arrangements from maps (it's a link tile)
  const exportableMaps = useMemo(() => 
    maps.filter(m => m.id !== 'formal-arrangements'), 
    [maps]
  );

  // Use context data with fallbacks
  const willStatus = useMemo(() => contextWillStatus || {}, [contextWillStatus]);
  const contacts = useMemo(() => contextContacts || [], [contextContacts]);

  // Will status augmented with the separately-loaded sub-tables, mapped to the
  // exact shapes the existing PDF/CSV Will section reads. This is the Part A fix:
  // the PDF previously read willStatus.poaHolders/.otherAdvisors/.otherDocuments,
  // which the context always leaves empty.
  const willStatusForExport = useMemo(() => ({
    ...willStatus,
    poaHolders: poaHolders.map(h => ({
      name: h.name, relationship: h.role || '', email: h.email || '', phone: h.phone || '',
    })),
    otherAdvisors: otherAdvisors.map(a => ({
      type: a.type || '', name: a.name, email: a.email || '', phone: a.phone || '', notes: '',
    })),
    otherDocuments: formalArrangements.map(a => ({
      type: a.type, heldWith: a.held_with || '', notes: a.notes || '',
    })),
    children: (willStatus.children && willStatus.children.length)
      ? willStatus.children
      : childrenRows.map(c => ({
          name: c.name, dob: c.date_of_birth || '',
          intendedGuardianName: c.intended_guardian_name || '',
          godparent1Name: '', arrangementStatus: '', specialNeeds: '', notes: c.notes || '',
        })),
  }), [willStatus, poaHolders, otherAdvisors, formalArrangements, childrenRows]);

  // Fetch all data when component mounts
  useEffect(() => {
    const loadData = async () => {
      if (isAuthenticated && !dataLoaded) {
        try {
          const [, , , poaRes, advRes, faRes, childRes] = await Promise.all([
            fetchMaps(),
            fetchContacts(),
            fetchWillStatus(),
            // These live in their own tables and are NOT carried on the context
            // willStatus object — load them directly so the export includes them.
            poaHoldersAPI.list(),
            otherAdvisorsAPI.list(),
            formalArrangementsAPI.list(),
            childrenAPI.list(),
          ]);
          setPoaHolders(poaRes?.data || []);
          setOtherAdvisors(advRes?.data || []);
          setFormalArrangements(faRes?.data || []);
          setChildrenRows(childRes?.children || childRes?.data || []);
          setDataLoaded(true);
        } catch (err) {
          console.error('Failed to load export data:', err);
        }
      }
    };
    loadData();
  }, [isAuthenticated, dataLoaded, fetchMaps, fetchContacts, fetchWillStatus]);

  // Fetch entries for all maps after maps are loaded
  useEffect(() => {
    const loadAllEntries = async () => {
      if (dataLoaded && exportableMaps.length > 0 && !entriesLoaded) {
        setLoadingEntries(true);
        try {
          const entriesMap = {};
          const notesMap = {};
          const wishesMap = {};
          
          for (const map of exportableMaps) {
            const entries = await fetchEntriesForMap(map.id);
            entriesMap[map.id] = entries || [];
            notesMap[map.id] = getMapNotes(map.id) || '';
            wishesMap[map.id] = getMapWishes(map.id) || null;
          }
          
          setAllEntries(entriesMap);
          setAllMapNotes(notesMap);
          setAllMapWishes(wishesMap);
          setEntriesLoaded(true);
        } catch (err) {
          console.error('Failed to load entries:', err);
        } finally {
          setLoadingEntries(false);
        }
      }
    };
    loadAllEntries();
  }, [dataLoaded, exportableMaps, entriesLoaded, fetchEntriesForMap, getMapNotes, getMapWishes]);

  // Update notes and wishes when maps data changes
  useEffect(() => {
    if (entriesLoaded && exportableMaps.length > 0) {
      const notesMap = {};
      const wishesMap = {};
      exportableMaps.forEach(map => {
        notesMap[map.id] = getMapNotes(map.id) || '';
        wishesMap[map.id] = getMapWishes(map.id) || null;
      });
      setAllMapNotes(notesMap);
      setAllMapWishes(wishesMap);
    }
  }, [entriesLoaded, exportableMaps, getMapNotes, getMapWishes]);

  const handleToggleMap = (mapId) => {
    setSelectedMaps(prev => ({ ...prev, [mapId]: !prev[mapId] }));
  };

  const handleSelectAllMaps = () => {
    const allSelected = Object.values(selectedMaps).filter(Boolean).length === exportableMaps.length;
    if (allSelected) {
      setSelectedMaps({});
    } else {
      const newSelected = {};
      exportableMaps.forEach(m => { newSelected[m.id] = true; });
      setSelectedMaps(newSelected);
    }
  };

  const selectedMapCount = Object.values(selectedMaps).filter(Boolean).length;
  const hasAnySelection = selectedMapCount > 0 || includeWillStatus || includeContacts;

  // Calculate totals
  const totalEntries = Object.values(allEntries).reduce((sum, entries) => sum + entries.length, 0);
  const selectedEntries = exportableMaps
    .filter(m => selectedMaps[m.id])
    .reduce((sum, m) => sum + (allEntries[m.id]?.length || 0), 0);

  // Get user's full name from Clerk
  const userName = useMemo(() => {
    if (user?.firstName && user?.lastName) return `${user.firstName} ${user.lastName}`;
    return user?.firstName || user?.fullName || 'Estate Owner';
  }, [user]);

  // Get export data based on selections
  const getExportData = useCallback(() => {
    return {
      maps: exportableMaps.filter(m => selectedMaps[m.id]),
      entries: allEntries,
      willStatus: includeWillStatus ? willStatusForExport : null,
      contacts: includeContacts ? contacts : null,
      mapNotes: allMapNotes,
      mapWishes: allMapWishes,
      userName,
    };
  }, [exportableMaps, selectedMaps, allEntries, includeWillStatus, willStatusForExport, includeContacts, contacts, allMapNotes, allMapWishes, userName]);

  // Export handlers
  const handleExportPDF = useCallback(async () => {
    if (!hasAnySelection) {
      toast.error('Please select at least one item to export');
      return;
    }
    setExporting(true);
    try {
      generatePDF(getExportData());
    } catch (err) {
      console.error('PDF generation failed:', err);
      toast.error('Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  }, [hasAnySelection, getExportData]);

  const handleLawyerExport = useCallback(async () => {
    // Select all data
    const newSelected = {};
    exportableMaps.forEach(m => { newSelected[m.id] = true; });
    setSelectedMaps(newSelected);
    setIncludeWillStatus(true);
    setIncludeContacts(true);

    // Build the export data directly — no need to wait for state updates
    const data = {
      maps: exportableMaps,
      entries: allEntries,
      willStatus: willStatusForExport,
      contacts: contacts,
      mapNotes: allMapNotes,
      mapWishes: allMapWishes,
      userName,
    };

    setExporting(true);
    try {
      generatePDF(data);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  }, [exportableMaps, allEntries, willStatusForExport, contacts, allMapNotes, allMapWishes, userName]);

  // Contact-specific export - filters entries where the contact is mentioned in wishes
  const handleContactExport = useCallback(async () => {
    if (!selectedContactId) {
      toast.error('Please select a contact');
      return;
    }
    
    const contact = contacts.find(c => c.id === selectedContactId);
    if (!contact) {
      toast.error('Contact not found');
      return;
    }
    
    // Find all maps where this contact is mentioned in wishes
    const relevantMaps = [];
    const relevantEntries = {};
    
    exportableMaps.forEach(map => {
      const wishes = allMapWishes[map.id];
      const mapEntries = allEntries[map.id] || [];
      
      // Check if contact is in wishes for this map
      const isInWishes = wishes?.contactId === selectedContactId || 
                         wishes?.contactName?.toLowerCase() === contact.name?.toLowerCase();
      
      // Also check if contact is mentioned in any entry's firstAction or notes
      const relevantMapEntries = mapEntries.filter(entry => {
        const firstAction = (entry.firstAction || '').toLowerCase();
        const notes = (entry.notes || '').toLowerCase();
        const contactName = (contact.name || '').toLowerCase();
        return firstAction.includes(contactName) || notes.includes(contactName);
      });
      
      if (isInWishes || relevantMapEntries.length > 0) {
        relevantMaps.push(map);
        relevantEntries[map.id] = isInWishes ? mapEntries : relevantMapEntries;
      }
    });
    
    if (relevantMaps.length === 0) {
      toast.error(`No assets found linked to ${contact.name}`);
      return;
    }
    
    setExporting(true);
    try {
      generatePDF({
        maps: relevantMaps,
        entries: relevantEntries,
        willStatus: null,
        contacts: [contact],
        mapNotes: allMapNotes,
        mapWishes: allMapWishes,
        userName,
      });
      toast.success(`Exported items for ${contact.name}`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  }, [selectedContactId, contacts, exportableMaps, allMapWishes, allEntries, allMapNotes, userName]);

  // Summary export — assemble the digest once, send the REDACTED text to the AI,
  // then show the narrative in a preview. Download builds the PDF =
  // [AI narrative] + [deterministic Details from the SAME digest].
  const handleGenerateSummary = useCallback(async () => {
    setSummaryGenerating(true);
    try {
      const { details, text } = assembleVaultDigest({
        maps: exportableMaps,
        entries: allEntries,
        mapNotes: allMapNotes,
        mapWishes: allMapWishes,
        willStatus,                 // context will-status (executors/holder/advisor/scope)
        poaHolders,                 // raw sub-tables (digest maps them itself)
        otherAdvisors,
        formalArrangements,
        children: childrenRows,
        contacts,
        userName,
      });
      // `text` is already redacted (REDACT_KEYS stripped); `details` is verbatim.
      const res = await aiAPI.generateSummary(text);
      setSummaryPreview({ summary: res.summary, details, text });
      setShowSummaryModal(true);
    } catch (err) {
      console.error('Summary generation failed:', err);
      const msg = err?.response?.data?.message || 'Failed to generate summary';
      toast.error(msg);
    } finally {
      setSummaryGenerating(false);
    }
  }, [exportableMaps, allEntries, allMapNotes, allMapWishes, willStatus, poaHolders, otherAdvisors, formalArrangements, childrenRows, contacts, userName]);

  // Re-POST the SAME (already redacted) digest for a fresh narrative.
  const handleRegenerateSummary = useCallback(async () => {
    if (!summaryPreview) return;
    setSummaryGenerating(true);
    try {
      const res = await aiAPI.generateSummary(summaryPreview.text);
      setSummaryPreview((prev) => (prev ? { ...prev, summary: res.summary } : prev));
    } catch (err) {
      console.error('Summary regeneration failed:', err);
      toast.error(err?.response?.data?.message || 'Failed to regenerate summary');
    } finally {
      setSummaryGenerating(false);
    }
  }, [summaryPreview]);

  // Build the PDF from the previewed narrative + the SAME deterministic details.
  const handleDownloadSummary = useCallback(() => {
    if (!summaryPreview) return;
    generateSummaryPDF({ summary: summaryPreview.summary, details: summaryPreview.details, userName });
    setShowSummaryModal(false);
  }, [summaryPreview, userName]);

  // CSV Export
  const handleExportCSV = useCallback(() => {
    if (!hasAnySelection) {
      toast.error('Please select at least one item to export');
      return;
    }

    let csvContent = '';
    const date = new Date().toISOString().split('T')[0];

    // Will Status
    if (includeWillStatus && willStatus) {
      csvContent += 'WILL & LEGAL STATUS\n';
      csvContent += 'Field,Value\n';
      csvContent += `Will Exists,${escapeCSV(willStatus.willExists || 'Not specified')}\n`;
      if (willStatus.willExists === 'yes') {
        if (willStatus.dateCreated) csvContent += `Date Created,${escapeCSV(willStatus.dateCreated)}\n`;
        if (willStatus.dateUpdated) csvContent += `Last Updated,${escapeCSV(willStatus.dateUpdated)}\n`;
        if (willStatus.firmName || willStatus.heldWith) csvContent += `Held With,${escapeCSV(willStatus.firmName || willStatus.heldWith)}\n`;
        if (willStatus.location) csvContent += `Location,${escapeCSV(willStatus.location)}\n`;
      }
      if (willStatus.executor1?.name) csvContent += `Primary Executor,${escapeCSV(willStatus.executor1.name)}\n`;
      if (willStatus.executor2?.name) csvContent += `Secondary Executor,${escapeCSV(willStatus.executor2.name)}\n`;
      csvContent += '\n';

      // POA holders / advisors / formal arrangements / children (Part A — these
      // come from their own tables, not the always-empty willStatus.* arrays).
      if (poaHolders.length > 0) {
        csvContent += 'POA HOLDERS\nName,Role,Email,Phone\n';
        poaHolders.forEach(h => {
          csvContent += `${escapeCSV(h.name)},${escapeCSV(h.role)},${escapeCSV(h.email)},${escapeCSV(h.phone)}\n`;
        });
        csvContent += '\n';
      }
      if (otherAdvisors.length > 0) {
        csvContent += 'OTHER ADVISORS\nName,Type,Email,Phone\n';
        otherAdvisors.forEach(a => {
          csvContent += `${escapeCSV(a.name)},${escapeCSV(a.type)},${escapeCSV(a.email)},${escapeCSV(a.phone)}\n`;
        });
        csvContent += '\n';
      }
      if (formalArrangements.length > 0) {
        csvContent += 'FORMAL ARRANGEMENTS\nType,Held With,Notes\n';
        formalArrangements.forEach(a => {
          csvContent += `${escapeCSV(a.type)},${escapeCSV(a.held_with)},${escapeCSV(a.notes)}\n`;
        });
        csvContent += '\n';
      }
      if (childrenRows.length > 0) {
        csvContent += 'CHILDREN & DEPENDENTS\nName,Date of Birth,Intended Guardian,Notes\n';
        childrenRows.forEach(c => {
          csvContent += `${escapeCSV(c.name)},${escapeCSV(c.date_of_birth)},${escapeCSV(c.intended_guardian_name)},${escapeCSV(c.notes)}\n`;
        });
        csvContent += '\n';
      }
    }

    // Contacts
    if (includeContacts && contacts.length > 0) {
      csvContent += 'CONTACTS\n';
      csvContent += 'Name,Category,Role,Email,Phone,Notes\n';
      contacts.forEach(c => {
        csvContent += `${escapeCSV(c.name)},${escapeCSV(c.category)},${escapeCSV(c.role || c.relationship)},${escapeCSV(c.email)},${escapeCSV(c.phone)},${escapeCSV(c.notes)}\n`;
      });
      csvContent += '\n';
    }

    // Maps
    const selectedMapsList = exportableMaps.filter(m => selectedMaps[m.id]);
    selectedMapsList.forEach(map => {
      const mapEntries = allEntries[map.id] || [];
      const notes = allMapNotes[map.id];
      const wishes = allMapWishes[map.id];
      const fields = getFieldsForMap(map.id);

      csvContent += `\n${map.name.toUpperCase()}\n`;
      if (notes) csvContent += `Notes & Intent:,${escapeCSV(notes)}\n`;
      if (wishes?.contactName) csvContent += `Intended For:,${escapeCSV(wishes.contactName)}\n`;
      if (wishes?.notes) csvContent += `Wishes:,${escapeCSV(wishes.notes)}\n`;
      
      if (mapEntries.length > 0) {
        csvContent += fields.map(f => escapeCSV(f.label)).join(',') + '\n';
        mapEntries.forEach(entry => {
          csvContent += fields.map(field => escapeCSV(getFieldValue(entry, field, map.id) || '')).join(',') + '\n';
        });
      } else {
        csvContent += 'No entries recorded\n';
      }
      csvContent += '\n';
    });

    generateCSV(csvContent, `lifely-export-${date}.csv`);
    toast.success('CSV exported successfully');
  }, [hasAnySelection, includeWillStatus, willStatus, includeContacts, contacts, exportableMaps, selectedMaps, allEntries, allMapNotes, allMapWishes, poaHolders, otherAdvisors, formalArrangements, childrenRows]);

  // Loading state
  const isLoading = loading.maps || loading.contacts || loading.willStatus;
  if ((isLoading && !dataLoaded) || loadingEntries) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">
          {loadingEntries ? 'Loading asset details...' : 'Loading your data...'}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="print-export-page">
      {/* Header */}
      <section className="space-y-2 border-b border-border pb-6 mb-10">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-foreground">Export</h1>
        <p className="text-sm text-muted-foreground">
          Create a shareable summary of your estate information, for your lawyer, your executor, or a specific person.
        </p>
      </section>

      {/* (1) Full Estate Summary (PDF) — featured */}
      <Card className="border-2 border-primary/40 border-l-4 border-l-primary bg-primary/10">
        <CardContent className="p-7 space-y-4">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <FileText className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-heading font-semibold text-foreground">Full Estate Summary (PDF)</h2>
              <p className="text-sm text-muted-foreground mt-1">
                A complete, lawyer-ready summary of your estate information including all maps, formal arrangements, and contacts.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <Badge variant="outline" className="bg-primary/10 text-primary border border-primary/20 text-xs font-medium px-2.5 py-1 rounded-full">Will & POA Status</Badge>
            <Badge variant="outline" className="bg-primary/10 text-primary border border-primary/20 text-xs font-medium px-2.5 py-1 rounded-full">All Contacts</Badge>
            <Badge variant="outline" className="bg-primary/10 text-primary border border-primary/20 text-xs font-medium px-2.5 py-1 rounded-full">Detailed Asset Tables</Badge>
            <Badge variant="outline" className="bg-primary/10 text-primary border border-primary/20 text-xs font-medium px-2.5 py-1 rounded-full">Notes & Wishes</Badge>
          </div>
          <Button
            onClick={() => requireExport(handleLawyerExport)}
            disabled={exporting}
            className="w-full sm:w-auto"
            data-testid="export-lawyer-button"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Download PDF
          </Button>
        </CardContent>
      </Card>

      {/* (2) Custom Export — secondary */}
      <Card className="border border-border">
        <CardContent className="p-7 space-y-5">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center shrink-0">
              <SlidersHorizontal className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-heading font-semibold text-foreground">Custom Export</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Choose exactly what to include. Export as PDF or CSV.
              </p>
            </div>
          </div>

          {/* Will & Contacts toggles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label
              htmlFor="will-status-switch"
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${includeWillStatus ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/30'}`}
              data-testid="will-status-toggle"
            >
              <div className="flex items-center gap-3">
                <ScrollText className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium text-sm">Will & Legal Status</p>
                  <p className="text-xs text-muted-foreground">Will, POA, executors</p>
                </div>
              </div>
              <Switch id="will-status-switch" checked={includeWillStatus} onCheckedChange={setIncludeWillStatus} />
            </label>
            <label
              htmlFor="contacts-switch"
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${includeContacts ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/30'}`}
              data-testid="contacts-toggle"
            >
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium text-sm">Contacts</p>
                  <p className="text-xs text-muted-foreground">{contacts.length} contacts</p>
                </div>
              </div>
              <Switch id="contacts-switch" checked={includeContacts} onCheckedChange={setIncludeContacts} />
            </label>
          </div>

          {/* Maps selection */}
          <Collapsible open={mapsExpanded} onOpenChange={setMapsExpanded}>
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between p-3 rounded-lg border border-border cursor-pointer hover:bg-muted/30">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">Asset Maps</p>
                    <p className="text-xs text-muted-foreground">
                      {selectedMapCount} of {exportableMaps.length} selected · {totalEntries} entries
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleSelectAllMaps(); }}
                    data-testid="select-all-maps-button"
                  >
                    {selectedMapCount === exportableMaps.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  {mapsExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {exportableMaps.map((map) => {
                  const entriesCount = allEntries[map.id]?.length || 0;
                  const hasNotes = !!allMapNotes[map.id];
                  const hasWishes = !!(allMapWishes[map.id]?.contactName || allMapWishes[map.id]?.notes);
                  const selected = selectedMaps[map.id] || false;

                  return (
                    <label
                      key={map.id}
                      htmlFor={`map-checkbox-${map.id}`}
                      className={`flex items-center gap-3 px-3 py-3 rounded-lg border transition-colors cursor-pointer ${selected ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/30'}`}
                    >
                      <Checkbox id={`map-checkbox-${map.id}`} checked={selected} onCheckedChange={() => handleToggleMap(map.id)} />
                      <span className="font-medium text-sm flex-1 min-w-0 truncate">{map.name}</span>
                      {hasNotes && <Badge variant="outline" className="text-[10px] h-4 px-1">Notes</Badge>}
                      {hasWishes && <Badge variant="outline" className="text-[10px] h-4 px-1 text-primary border-primary/30">Wishes</Badge>}
                      <Badge variant="secondary" className="text-[11px] h-5 px-1.5 tabular-nums shrink-0">{entriesCount}</Badge>
                    </label>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Summary + actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-border">
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{selectedMapCount} maps</span>
              {' · '}
              <span>{selectedEntries} entries</span>
              {includeWillStatus && ' · Will status'}
              {includeContacts && ` · ${contacts.length} contacts`}
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => requireExport(handleExportCSV)}
                disabled={!hasAnySelection || exporting}
                className="flex-1 sm:flex-none"
                data-testid="export-csv-button"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                CSV
              </Button>
              <Button
                onClick={() => requireExport(handleExportPDF)}
                disabled={!hasAnySelection || exporting}
                className="flex-1 sm:flex-none"
                data-testid="export-pdf-button"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* (3) Export for a Contact — secondary */}
      {contacts.length > 0 && (
        <Card className="border border-border">
          <CardContent className="p-7 space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-heading font-semibold text-foreground">Export for a Contact</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Generate a filtered summary for a specific contact showing only what they need to know.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Select value={selectedContactId} onValueChange={setSelectedContactId}>
                <SelectTrigger className="w-full sm:w-[260px]" data-testid="contact-select">
                  <SelectValue placeholder="Select a contact..." />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map(contact => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.name} {contact.category && `(${contact.category})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => requireExport(handleContactExport)}
                disabled={!selectedContactId || exporting}
                data-testid="export-contact-button"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Export for Contact
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Includes maps where this contact appears in your wishes or is mentioned in entry notes.
            </p>
          </CardContent>
        </Card>
      )}

      {/* (4) Summary — warm narrative + complete record */}
      <Card className="border border-border">
        <CardContent className="p-7 space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-heading font-semibold text-foreground">Summary</h2>
              <p className="text-sm text-muted-foreground mt-1">
                A warm, plain-English summary for your loved ones, followed by a complete record of everything you&apos;ve entered.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Button
              onClick={() => requireExport(handleGenerateSummary)}
              disabled={summaryGenerating}
              data-testid="export-summary-button"
            >
              {summaryGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {summaryGenerating ? 'Generating…' : 'Generate Summary'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Summary generated with AI from your entries. Sensitive recovery and access details are never sent.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Footnote */}
      <div className="flex gap-3 p-4 rounded-lg bg-muted/50 border border-border">
        <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          <strong className="text-foreground">PDF</strong> is formatted for printing and sharing; <strong className="text-foreground">CSV</strong> suits spreadsheets. Exports never include passwords or credentials, and every PDF ends with a legal disclaimer page.
        </p>
      </div>

      {/* Summary preview — narrative only; the downloaded PDF adds the full record */}
      <Dialog open={showSummaryModal} onOpenChange={setShowSummaryModal}>
        <DialogContent className="max-w-2xl" data-testid="summary-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Summary preview
            </DialogTitle>
            <DialogDescription>
              A warm, plain-English note for your loved ones, generated with AI from your entries.
            </DialogDescription>
          </DialogHeader>

          <div
            className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-foreground rounded-lg border border-border bg-muted/30 p-4"
            data-testid="summary-preview-text"
          >
            {summaryPreview?.summary}
          </div>

          <p className="text-xs text-muted-foreground">
            This preview shows the narrative only. The downloaded PDF also includes a complete record of everything you&apos;ve entered.
          </p>

          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            <Button
              variant="outline"
              onClick={handleRegenerateSummary}
              disabled={summaryGenerating}
              data-testid="summary-regenerate-button"
            >
              {summaryGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Regenerate
            </Button>
            <Button
              onClick={handleDownloadSummary}
              disabled={summaryGenerating || !summaryPreview}
              data-testid="summary-download-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Legal Disclaimer Modal */}
      <Dialog open={showDisclaimer} onOpenChange={setShowDisclaimer}>
        <DialogContent className="max-w-lg" data-testid="export-disclaimer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-primary" />
              Before you export
            </DialogTitle>
            <DialogDescription>
              This export contains sensitive personal and financial information. By downloading, you accept responsibility for handling it securely.
            </DialogDescription>
          </DialogHeader>

          {/* Obligations */}
          <div className="space-y-2.5">
            {[
              { Icon: Printer, text: 'Only print in a private, secure environment and collect all pages immediately.' },
              { Icon: Lock, text: 'If saving digitally, store it in an encrypted or password-protected location.' },
              { Icon: Share2, text: 'Only share with authorised people, your lawyer, executor, or immediate family.' },
              { Icon: Trash2, text: 'Shred printed copies and permanently delete digital copies when no longer needed.' },
            ].map(({ Icon, text }, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground pt-1">{text}</p>
              </div>
            ))}
          </div>

          {/* Liability */}
          <div className="rounded-lg bg-muted/60 border border-border p-3">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-semibold text-foreground">Limitation of liability: </span>
              Lifely cannot be held responsible for any loss, damage, or consequence arising from the printing, saving, sharing, misuse, or unauthorised access of this document. You assume full responsibility for its secure handling from the point of download. This export does not constitute legal or financial advice.
            </p>
          </div>

          {/* Acknowledgement */}
          <label
            htmlFor="disclaimer-ack"
            className="flex items-start gap-3 cursor-pointer rounded-lg border border-border p-3 hover:bg-muted/30"
          >
            <Checkbox
              id="disclaimer-ack"
              checked={disclaimerAck}
              onCheckedChange={setDisclaimerAck}
              className="mt-0.5"
              data-testid="disclaimer-ack-checkbox"
            />
            <span className="text-sm text-foreground">
              I understand and accept responsibility for handling this export securely.
            </span>
          </label>

          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            <Button
              variant="outline"
              onClick={() => { setShowDisclaimer(false); pendingExportRef.current = null; }}
              data-testid="disclaimer-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDisclaimerConfirm}
              disabled={!disclaimerAck}
              data-testid="disclaimer-confirm-button"
            >
              Download export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade prompt when a non-paying user tries to export */}
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} trigger="export" />
    </div>
  );
};

export default PrintExport;
